import { DEFAULT_LANGUAGE } from "../../core/config";

const language = DEFAULT_LANGUAGE.code;

const hourAndMinutesFormattingOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

const dayAndMonthAndYearFormattingOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

export enum MaintenanceWorkStatus {
  Ongoing = 'ONGOING',
  Planned = 'PLANNED'
}

export interface MaintenanceWork {
  startTime: string;
  endTime: string;
  status: MaintenanceWorkStatus;
}

export const formatMaintenanceWorks = (maintenanceWorks: MaintenanceWork[]): string | undefined => {
  if (maintenanceWorks.length) {
    const ongoingMaintenanceWork = findOngoingMaintenanceWork(maintenanceWorks);
    if (ongoingMaintenanceWork === null) {
      const plannedMaintenanceWork = getClosestMaintenanceWorkScheduledInFuture(maintenanceWorks);
      return plannedMaintenanceWork === null
        ? undefined
        : formatMaintenanceWork(plannedMaintenanceWork);
    } else {
      return formatMaintenanceWork(ongoingMaintenanceWork);
    }
  } else {
    return undefined;
  }
};

const findOngoingMaintenanceWork = (maintenanceWorks: MaintenanceWork[]): MaintenanceWork | null => {
  const now = Date.now();
  const ongoingMaintenanceWork = maintenanceWorks
    .find(maintenanceWork => isMaintenanceWorkInProgress(maintenanceWork, now));
  return ongoingMaintenanceWork || null;
};

const isMaintenanceWorkInProgress = (maintenanceWork: MaintenanceWork, referenceTime: number): boolean =>
  new Date(maintenanceWork.startTime).getTime() <= referenceTime && referenceTime <= new Date(maintenanceWork.endTime).getTime();

const getClosestMaintenanceWorkScheduledInFuture = (works: MaintenanceWork[]): MaintenanceWork | null => {
  return works
    .filter(work => new Date(work.startTime).getTime() >= Date.now())
    .sort((previous, next) => previous.startTime.localeCompare(next.startTime))
    ?.[0] || null;
};

const formatMaintenanceWork = (maintenanceWork: MaintenanceWork): string => {
  const startTime = maintenanceWork.startTime;
  const endTime = maintenanceWork.endTime;
  return isStartDayAndEndDayEqual(startTime, endTime)
    ? `${formatDateToDayAndMonthAndYear(startTime)} | ${formatDateToHourAndMinutes(startTime)} - ${formatDateToHourAndMinutes(endTime)}`
    : `${formatDateToFullTimeAndDay(startTime)} - ${formatDateToFullTimeAndDay(endTime)}`;
};

const formatDateToHourAndMinutes = (date: string): string =>
  new Date(date).toLocaleTimeString(language, hourAndMinutesFormattingOptions);


const formatDateToDayAndMonthAndYear = (date: string): string =>
  new Date(date).toLocaleDateString(language, dayAndMonthAndYearFormattingOptions);

const formatDateToFullTimeAndDay = (date: string): string =>
  new Date(date).toLocaleDateString(
    language, { ...hourAndMinutesFormattingOptions, ...dayAndMonthAndYearFormattingOptions });

const isStartDayAndEndDayEqual = (startTime: string, endTime: string): boolean =>
  formatDateToFullDate(startTime) === formatDateToFullDate(endTime);

const formatDateToFullDate = (date: string): string =>
  new Date(date).toLocaleDateString(language, { year: 'numeric', month: '2-digit', day: '2-digit' });